/**
 * Cart Reset Warning Module
 * 
 * Intercepts delivery preference changes and shows a warning modal
 * if the cart contains items. Provides cart clearing functionality
 * or delegates to variant switching based on settings.
 * 
 * Listens for: deliveryPreference:beforeSave
 * Dispatches: cartResetWarning:confirmed, cartResetWarning:cancelled
 */

(function() {
  'use strict';

  let pendingPreference = null;
  let pendingSaveCallback = null;
  // True once the pending change has been resolved through pendingSaveCallback,
  // so a failed clear followed by Cancel cannot resolve the same change twice.
  let pendingSaveResolved = false;

  const TOAST_DURATION_MS = 4000;
  let toastTimer = null;

  /**
   * Check if cart reset warning is enabled in theme settings
   * @returns {boolean}
   */
  function isEnabled() {
    return window.CartResetWarningSettings?.enabled !== false;
  }

  /**
   * Show a toast using the shared #CartToastNotification element
   * rendered by snippets/cart-reset-warning-modal.liquid.
   * Falls back to a native alert when that markup is not on the page.
   * @param {string} message - Message to display
   * @param {boolean} [isError] - Render the failure variant of the toast
   */
  function showToast(message, isError) {
    const toast = document.getElementById('CartToastNotification');
    const messageEl = toast ? toast.querySelector('.cart-toast-notification__message') : null;

    if (!toast || !messageEl) {
      alert(message);
      return;
    }

    messageEl.textContent = message;
    toast.classList.toggle('cart-toast-notification--error', isError === true);
    toast.classList.add('active');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function() {
      toast.classList.remove('active');
    }, TOAST_DURATION_MS);
  }

  /**
   * Show success notification to user
   * @param {string} message - Message to display
   */
  function showSuccessNotification(message) {
    showToast(message);
  }

  /**
   * Get cart data from Shopify
   * @returns {Promise<Object>} Cart data
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Clear the cart using Shopify's clear.js endpoint
   * @returns {Promise<Object>} Empty cart response
   */
  async function clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to clear cart: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  /**
   * Check if delivery preference is actually changing
   * @param {Object} currentPref - Current preference
   * @param {Object} newPref - New preference
   * @returns {boolean}
   */
  function isPreferenceChanging(currentPref, newPref) {
    if (currentPref.deliveryType !== newPref.deliveryType) {
      return true;
    }

    if (newPref.deliveryType === 'STORE' && 
        currentPref.deliveryLocation !== newPref.deliveryLocation) {
      return true;
    }

    return false;
  }

  /**
   * Populate the warning modal with line items
   * @param {Array} items - Cart line items
   */
  function populateLineItems(items) {
    const itemsContainer = document.querySelector('.cart-reset-warning-modal__items');
    if (!itemsContainer) return;

    itemsContainer.innerHTML = '';

    items.forEach(item => {
      const itemEl = document.createElement('div');
      itemEl.className = 'cart-reset-warning-modal__item';

      // Get image URL
      const imageUrl = item.featured_image?.url || item.image || '';
      
      // Get variant title or use product title.
      // Display only: strip the " - {Store}" suffix so store identification never
      // reaches customer-facing wording. The raw item.variant_title from /cart.js is
      // untouched — cart-store-switcher.js:41-54 still needs the suffix to match stores.
      const rawVariantTitle = item.variant_title || '';
      const variantTitle = rawVariantTitle.split(' - ')[0];
      const productTitle = item.product_title || item.title || '';
      
      itemEl.innerHTML = `
        ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" class="cart-reset-warning-modal__item-image">` : ''}
        <div class="cart-reset-warning-modal__item-details">
          <div class="cart-reset-warning-modal__item-title">${productTitle}</div>
          ${variantTitle ? `<div class="cart-reset-warning-modal__item-variant">${variantTitle}</div>` : ''}
          <div class="cart-reset-warning-modal__item-quantity">Qty: ${item.quantity}</div>
        </div>
      `;

      itemsContainer.appendChild(itemEl);
    });
  }

  /**
   * Show the warning modal
   * @param {Array} items - Cart line items
   */
  function showWarningModal(items) {
    const modal = document.getElementById('CartResetWarning');
    if (!modal) {
      console.error('Cart reset warning modal not found');
      return;
    }

    // Populate line items
    populateLineItems(items);

    // Show the modal
    modal.show();
  }

  /**
   * Hide the warning modal
   */
  function hideWarningModal() {
    const modal = document.getElementById('CartResetWarning');
    if (modal) {
      modal.hide();
    }
  }

  /**
   * Show loading state in modal
   */
  function showLoading() {
    const itemsContainer = document.querySelector('.cart-reset-warning-modal__items');
    const loadingContainer = document.querySelector('.cart-reset-warning-modal__loading');
    const buttons = document.querySelectorAll('.cart-reset-warning-modal__confirm, .cart-reset-warning-modal__cancel');

    if (itemsContainer) itemsContainer.style.display = 'none';
    if (loadingContainer) loadingContainer.style.display = 'flex';
    
    buttons.forEach(btn => btn.disabled = true);
  }

  /**
   * Hide loading state in modal
   */
  function hideLoading() {
    const itemsContainer = document.querySelector('.cart-reset-warning-modal__items');
    const loadingContainer = document.querySelector('.cart-reset-warning-modal__loading');
    const buttons = document.querySelectorAll('.cart-reset-warning-modal__confirm, .cart-reset-warning-modal__cancel');

    if (itemsContainer) itemsContainer.style.display = 'block';
    if (loadingContainer) loadingContainer.style.display = 'none';
    
    buttons.forEach(btn => btn.disabled = false);
  }

  /**
   * Update cart icon bubble in header
   */
  async function updateCartIconBubble() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    try {
      const url = `${rootUrl}?section_id=cart-icon-bubble`;
      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Get the cart icon bubble container
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        if (!cartIconBubble) return;
        
        // Find cart bubble in response
        const newBubble = doc.querySelector('.cart-count-bubble');
        const currentBubble = cartIconBubble.querySelector('.cart-count-bubble');
        
        if (newBubble && currentBubble) {
          // Update existing bubble with new count
          currentBubble.innerHTML = newBubble.innerHTML;
        } else if (!newBubble && currentBubble) {
          // Cart is now empty - remove the bubble
          currentBubble.remove();
          
          // Also swap the cart icon from filled to empty
          const filledIcon = cartIconBubble.querySelector('.icon-cart');
          if (filledIcon) {
            // Fetch the empty cart icon from the response
            const emptyIcon = doc.querySelector('.icon-cart-empty');
            if (emptyIcon) {
              filledIcon.replaceWith(emptyIcon.cloneNode(true));
            }
          }
        } else if (newBubble && !currentBubble) {
          // Cart now has items - add the bubble
          const visuallyHidden = cartIconBubble.querySelector('.visually-hidden');
          if (visuallyHidden) {
            visuallyHidden.insertAdjacentElement('afterend', newBubble.cloneNode(true));
          } else {
            cartIconBubble.appendChild(newBubble.cloneNode(true));
          }
          
          // Also swap the cart icon from empty to filled
          const emptyIcon = cartIconBubble.querySelector('.icon-cart-empty');
          if (emptyIcon) {
            const filledIcon = doc.querySelector('.icon-cart');
            if (filledIcon) {
              emptyIcon.replaceWith(filledIcon.cloneNode(true));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating cart icon bubble:', error);
    }
  }

  /**
   * Update cart sections on page (if on cart page)
   */
  async function updateCartSections() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const isCartPage = window.location.pathname.includes('/cart');
    
    // Always update cart icon bubble
    await updateCartIconBubble();
    
    // Only update cart sections if on cart page
    if (isCartPage) {
      const sectionsToRefresh = ['main-cart-items', 'main-cart-footer'];
      
      for (const sectionId of sectionsToRefresh) {
        try {
          const url = `${rootUrl}?section_id=${sectionId}`;
          const response = await fetch(url);
          if (response.ok) {
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            const sectionElement = doc.getElementById(sectionId) || 
                                  doc.querySelector(`[data-id="${sectionId}"]`) ||
                                  doc.querySelector(`.${sectionId}`);
            
            if (sectionElement) {
              const targetElement = document.getElementById(sectionId) || 
                                   document.querySelector(`[data-id="${sectionId}"]`) ||
                                   document.querySelector(`.${sectionId}`);
              
              if (targetElement) {
                targetElement.innerHTML = sectionElement.innerHTML;
              }
            }
          }
        } catch (error) {
          console.error(`Error updating section ${sectionId}:`, error);
        }
      }
      
      // Update empty state
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.classList.add('is-empty');
      }

      document.querySelectorAll('[id="main-cart-footer"]').forEach(
        function(el) { el.classList.add('is-empty'); }
      );
      document.querySelectorAll('.alpine-shipping-estimate__section').forEach(
        function(el) { el.style.display = 'none'; }
      );
      document.querySelectorAll('.cart-gift-order-form-container').forEach(
        function(el) { el.style.display = 'none'; }
      );
      document.querySelectorAll('.cart__footer-wrapper').forEach(
        function(el) { el.style.display = 'none'; }
      );
      // Afterpay messaging widget
      document.querySelectorAll('square-placement').forEach(
        function(el) { el.style.display = 'none'; }
      );
    }
    
    console.log('[Cart Reset Warning] Cart UI refreshed without page reload');
  }

  /**
   * Handle confirmation - clear cart and proceed
   */
  async function handleConfirm() {
    if (!pendingPreference || !pendingSaveCallback) {
      console.error('No pending preference or callback');
      return;
    }

    try {
      // Show loading state
      showLoading();

      // Clear the cart
      await clearCart();

      // Proceed with the delivery preference change
      if (window.DeliverySelector) {
        window.DeliverySelector.setDeliveryPreference(
          pendingPreference.deliveryType,
          pendingPreference.deliveryLocation
        );
      }

      // Close modals
      hideWarningModal();
      
      // Close delivery selector modal
      const deliveryModal = document.getElementById('DeliverySelectorModal');
      if (deliveryModal) {
        deliveryModal.hide();
      }

      // Dispatch confirmation event
      document.dispatchEvent(new CustomEvent('cartResetWarning:confirmed', {
        detail: pendingPreference
      }));

      // Call the callback with success
      if (pendingSaveCallback) {
        pendingSaveCallback(true);
      }

      // Update cart UI without page reload
      await updateCartSections();

      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Show the single success confirmation for this fulfilment change
      const clearedMessage = window.CartResetWarningSettings?.translations?.successCleared || 'Cart cleared successfully';
      showSuccessNotification(clearedMessage);

      // Clear pending state
      pendingPreference = null;
      pendingSaveCallback = null;
      pendingSaveResolved = false;

    } catch (error) {
      console.error('Error clearing cart:', error);

      // Report the failure on the same toast surface a success uses, in the
      // storefront's language, and tell the delivery selector modal the change
      // did not happen so nothing is persisted. The warning modal stays open with
      // its pending change intact, so Confirm retries and Cancel backs out.
      const clearFailureMessage = window.CartResetWarningSettings?.translations?.clearFailed || 'Failed to clear cart. Please try again.';
      showToast(clearFailureMessage, true);

      if (pendingSaveCallback && !pendingSaveResolved) {
        pendingSaveResolved = true;
        pendingSaveCallback(false);
      }

      hideLoading();
    }
  }

  /**
   * Handle cancellation - close warning modal, return to delivery selector
   */
  function handleCancel() {
    // Close warning modal
    hideWarningModal();

    // Dispatch cancellation event
    document.dispatchEvent(new CustomEvent('cartResetWarning:cancelled'));

    // Call the callback with failure, unless a failed clear attempt already
    // resolved this change — one pending change resolves exactly once.
    if (pendingSaveCallback && !pendingSaveResolved) {
      pendingSaveCallback(false);
    }

    // Clear pending state
    pendingPreference = null;
    pendingSaveCallback = null;
    pendingSaveResolved = false;
  }

  /**
   * Show loading state in delivery selector modal
   */
  function showDeliveryModalLoading() {
    const loadingOverlay = document.querySelector('.delivery-selector-modal__loading');
    const saveButton = document.querySelector('.delivery-selector-modal__save');

    if (loadingOverlay) {
      loadingOverlay.style.display = 'flex';
    }
    if (saveButton) {
      saveButton.disabled = true;
    }
  }

  /**
   * Hide loading state in delivery selector modal
   */
  function hideDeliveryModalLoading() {
    const loadingOverlay = document.querySelector('.delivery-selector-modal__loading');
    const saveButton = document.querySelector('.delivery-selector-modal__save');

    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
    if (saveButton) {
      saveButton.disabled = false;
    }
  }

  /**
   * Report a fulfilment change that did not complete: one failure message on the
   * shared toast surface, the previously-active preference put back so nothing
   * is persisted, and the modal's save callback told the change did not happen.
   * @param {Object|null} previousPreference - Preference in force before the change
   * @param {Function} callback - The delivery selector modal's save callback
   */
  function reportFailedChange(previousPreference, callback) {
    if (previousPreference && window.DeliverySelector) {
      window.DeliverySelector.setDeliveryPreference(
        previousPreference.deliveryType,
        previousPreference.deliveryLocation,
        previousPreference.storeData,
        previousPreference.corporateAddress,
        { revert: true }
      );
    }

    const failureMessage = window.CartResetWarningSettings?.translations?.updateFailed || 'Failed to update cart. Please try again.';
    showToast(failureMessage, true);

    callback(false);
  }

  /**
   * Handle variant switching when setting is disabled
   * @param {Object} newPreference - New delivery preference
   * @param {Function} callback - Callback to call with true/false
   */
  async function handleVariantSwitching(newPreference, callback) {
    console.log('[Cart Reset Warning] Triggering variant switching');
    
    const isCartPage = window.location.pathname.includes('/cart');
    console.log('[Cart Reset Warning] On cart page:', isCartPage);

    // The option in force before anything is written, so a change that fails can
    // be put back exactly as it was.
    const previousPreference = window.DeliverySelector
      ? window.DeliverySelector.getDeliveryPreference()
      : null;
    
    const deliveryModal = document.getElementById('DeliverySelectorModal');
    
    // Show loading spinner
    console.log('[Cart Reset Warning] Showing loading spinner');
    showDeliveryModalLoading();
    
    if (isCartPage) {
      // On cart page - wait for variant switching to complete
      console.log('[Cart Reset Warning] Waiting for variant switching to complete');
      
      // Set up listener for completion event
      const completionPromise = new Promise((resolve) => {
        const handler = function(e) {
          console.log('[Cart Reset Warning] Variant switching complete event received', e.detail);
          document.removeEventListener('variantSwitching:complete', handler);
          resolve(e.detail);
        };
        document.addEventListener('variantSwitching:complete', handler);
        
        // Timeout after 30 seconds to prevent hanging
        setTimeout(() => {
          document.removeEventListener('variantSwitching:complete', handler);
          resolve({ success: false, error: 'timeout' });
        }, 30000);
      });
      
      // Update the delivery preference
      // This will dispatch 'deliveryPreference:changed' event
      if (window.DeliverySelector) {
        window.DeliverySelector.setDeliveryPreference(
          newPreference.deliveryType,
          newPreference.deliveryLocation
        );
      }
      
      // Wait for variant switching to complete
      const result = await completionPromise;
      console.log('[Cart Reset Warning] Variant switching result:', result);
      
      // Hide loading and close modal
      hideDeliveryModalLoading();
      
      if (deliveryModal) {
        deliveryModal.hide();
      }
      
      // A change that did not complete reports the failure on the same toast
      // surface, puts the previously-active option back, and is never persisted.
      if (result.success !== true) {
        reportFailedChange(previousPreference, callback);
        return;
      }

      // Show the success toast only when the cart actually finished updating
      const successMessage = window.CartResetWarningSettings?.translations?.successUpdated || 'Cart updated successfully';
      showToast(successMessage);
      
      callback(true);
      
    } else {
      // Not on cart page - update preference, show brief loading, then close
      console.log('[Cart Reset Warning] Not on cart page - saving preference');
      
      // Set up listener for completion event
      const completionPromise = new Promise((resolve) => {
        const handler = function(e) {
          console.log('[Cart Reset Warning] Variant switching complete event received', e.detail);
          document.removeEventListener('variantSwitching:complete', handler);
          resolve(e.detail);
        };
        document.addEventListener('variantSwitching:complete', handler);
        
        // Timeout after 30 seconds to prevent hanging
        setTimeout(() => {
          document.removeEventListener('variantSwitching:complete', handler);
          resolve({ success: false, error: 'timeout' });
        }, 30000);
      });
      
      // Update the delivery preference
      if (window.DeliverySelector) {
        window.DeliverySelector.setDeliveryPreference(
          newPreference.deliveryType,
          newPreference.deliveryLocation
        );
      }
      
      // Wait for variant switching to complete
      const result = await completionPromise;
      
      // Hide loading and close modal
      hideDeliveryModalLoading();
      
      if (deliveryModal) {
        deliveryModal.hide();
      }
      
      // A change that did not complete reports the failure on the same toast
      // surface, puts the previously-active option back, and is never persisted.
      if (result.success !== true) {
        reportFailedChange(previousPreference, callback);
        return;
      }

      // Show the success toast only when the cart actually finished updating
      const successMessage = window.CartResetWarningSettings?.translations?.successUpdated || 'Cart updated successfully';
      showToast(successMessage);
      
      callback(true);
    }
  }

  /**
   * Check cart and show warning if needed
   * @param {Object} newPreference - New delivery preference
   * @param {Function} callback - Callback to call with true/false
   */
  async function checkAndWarn(newPreference, callback) {
    try {
      // Get current preference
      const currentPreference = window.DeliverySelector?.getDeliveryPreference() || {
        deliveryType: 'DELIVERY',
        deliveryLocation: null
      };

      // Check if preference is actually changing
      if (!isPreferenceChanging(currentPreference, newPreference)) {
        // No change, proceed normally
        console.log('[Cart Reset Warning] No preference change detected, proceeding');
        callback(true);
        return;
      }

      // Get cart data
      const cart = await getCart();

      // If cart is empty, proceed normally - no need to clear or switch variants
      if (!cart.items || cart.items.length === 0) {
        console.log('[Cart Reset Warning] Cart is empty, proceeding without cart manipulation');
        callback(true);
        return;
      }

      // Cart has items - check which mode to use
      const settingEnabled = isEnabled();
      console.log('[Cart Reset Warning] Setting enabled:', settingEnabled);
      console.log('[Cart Reset Warning] Cart items:', cart.items.length);
      
      if (!settingEnabled) {
        const isStoreToDelivery = currentPreference.deliveryType === 'STORE'
          && newPreference.deliveryType === 'DELIVERY';

        if (isStoreToDelivery) {
          console.log('[Cart Reset Warning] Setting disabled, store-to-delivery - triggering variant switching');
          await handleVariantSwitching(newPreference, callback);
          return;
        }
        // Store->Store or Delivery->Store: fall through to show warning modal + clear cart
        console.log('[Cart Reset Warning] Setting disabled, not store-to-delivery - clearing cart');
      }

      // Show warning modal for cart clearing
      pendingPreference = newPreference;
      pendingSaveCallback = callback;
      pendingSaveResolved = false;

      showWarningModal(cart.items);

    } catch (error) {
      console.error('Error checking cart:', error);
      // If there's an error fetching cart, proceed anyway (fail-open)
      callback(true);
    }
  }

  /**
   * Initialize the cart reset warning module
   */
  function init() {
    console.log('[Cart Reset Warning] Module initialized');
    console.log('[Cart Reset Warning] Settings:', window.CartResetWarningSettings);
    
    // Listen for before save event from delivery selector modal
    document.addEventListener('deliveryPreference:beforeSave', async function(e) {
      console.log('[Cart Reset Warning] beforeSave event received');
      const { newType, newLocation, callback } = e.detail;

      const newPreference = {
        deliveryType: newType,
        deliveryLocation: newLocation
      };

      await checkAndWarn(newPreference, callback);
    });

    // Set up modal button handlers
    const modal = document.getElementById('CartResetWarning');
    if (modal) {
      const confirmBtn = modal.querySelector('[data-action="confirm"]');
      const cancelBtn = modal.querySelector('[data-action="cancel"]');
      const closeBtn = modal.querySelector('#ModalClose-CartResetWarning');

      if (confirmBtn) {
        confirmBtn.addEventListener('click', handleConfirm);
      }

      if (cancelBtn) {
        cancelBtn.addEventListener('click', handleCancel);
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', handleCancel);
      }
    }
  }

  // Export public API
  window.CartResetWarning = {
    isEnabled: isEnabled,
    checkAndWarn: checkAndWarn,
    clearCart: clearCart
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
